// eslint-disable-next-line
import React, { useState, useEffect } from 'react';
import { LuminaryList, LuminaryDataList, LuminaryData } from '../../types/luminary';
import { EnhancedTableHead } from '../../components/Table/EnhancedTableHead';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { HeadCell, Order } from '../../types/table';
import { getComparator, getSortComparator, stableSort } from '../../lib/table-helpers';
import * as PXBColors from '@brightlayer-ui/colors';
import * as MuiIcons from '@mui/icons-material';
import * as MuiCore from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import { LuminaryListItem } from '../../components/LuminaryList/LuminaryListItem';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { EmptyState } from '@brightlayer-ui/react-components';
import { ALL, DEFAULT_PAGE_ONE, RECORD_PER_PAGE_LIMIT } from '../../constants';
import { Thunks } from '../../actions';
import {
    Typography,
    Divider,
    Card,
    TableBody,
    ListSubheader,
    Table,
    TablePagination,
    Checkbox,
    SelectChangeEvent,
    Box,
    MenuItem,
    Collapse
} from '@mui/material';
import { DEVICE_STATUS, DEVICE_TYPE, DEVICE_TYPE_WIRELESS, getDeviceType, getStatusId, luminaryBitStatusEnum, luminaryBitStatusWirelessEnum, luminaryStatusEnum, WirelessLumTestGroupFilterDropdownItems } from '../enum/ENUM';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { systemDetailDefault } from './AssetDetails';
import { DeviceData, ZoneDetail } from '../../types/device';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { FilterDropdown } from '../../components/FilterDropdown/FilterDropdown';
import { ItemList } from '../../types/logbook-type';

type Props = {
    luminaryId: string;
    luminaryClick: Function;
    deviceId: string;
    deviceType?: string;
    selectedLines?: number[];
    selectedZones?: number[];
    selectedStatuses?: string[];
};

export const DeviceLuminariesList: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const { t } = useLanguageLocale();
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState('luminaryName');
    const [selectedLuminary, setSelectedLuminary] = React.useState('0');
    const [emptyLuminaryList, setEmptyLuminaryList] = React.useState(false);
    const [openATSDFilter, setOpenATSDFilter] = React.useState(false);
    const [openCircuitFilter, setOpenCircuitFilter] = React.useState(false);
    const [openStatusFilter, setOpenStatusFilter] = React.useState(false);
    const [openList, setOpenList] = React.useState(true);
    const asset = useSelector(Selectors.Dashboard.asset);
    const [luminaryList, setLuminaryList] = useState<LuminaryList>([]);
    const [luminaryDataList, setLuminaryDataList] = useState<LuminaryDataList>([]);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(RECORD_PER_PAGE_LIMIT);
    const [ATSDFilter, setATSDFilter] = React.useState<string[]>([]);
    const [statusFilter, setStatusFilter] = React.useState<string>('');
    const [statusFilterEnum, setStatusFilterEnum] = React.useState<string[]>([]);
    const [circuitFilter, setCircuitFilter] = React.useState<string[]>([]);
    const [ATSDFilterDropdownItems, setATSDFilterDropdownItems] = React.useState<string[]>([]);
    const [CircuitFilterDropdownItems, setCircuitFilterDropdownItems] = React.useState<string[]>([]);
    const [systemDetail, setSystemDetail] = useState<DeviceData>(systemDetailDefault);
    const [systemLines, setSystemLines] = useState<any[]>([]);
    const [systemLineZones, setSystemLineZones] = useState<any[]>([]);
    const [luminaryStatusList, setLuminaryStatusList] = useState<ItemList>([]);
    const [selectedSystemLines, setSelectedSystemLines] = useState<any[]>([]);
    const [selectedSystemLineZones, setSelectedSystemLineZones] = useState<any[]>([]);
    const [systemZones, setSystemZones] = useState<ZoneDetail[] | undefined>(undefined);
    const [testGroupFilter, setTestGroupFilter] = useState<string[]>([]);
    const testGroupDropdownItems = WirelessLumTestGroupFilterDropdownItems;

    React.useEffect(() => {
        if (asset.luminaryListLoading) {
            setOpenATSDFilter(false);
            setOpenCircuitFilter(false);
        }
    }, [asset.luminaryListLoading]);

    useEffect(() => {
        if (props.selectedLines) {
            setSelectedSystemLines(props.selectedLines);
        }
        if (props.selectedZones) {
            setSelectedSystemLineZones(props.selectedZones);
        }

        if (props.selectedStatuses) {
            setStatusFilterEnum(props.selectedStatuses);
        }
    }, [
        props.selectedLines,
        props.selectedZones,
        props.selectedStatuses,
    ])

    const handleTestGroupFilterChange = (event: SelectChangeEvent<typeof testGroupFilter>) => {
        const { target: { value } } = event;
        if (value.includes(t('FILTER.ALL'))) {
            if (testGroupFilter.length === testGroupDropdownItems.length) {
                setTestGroupFilter([]);
            } else {
                setTestGroupFilter(testGroupDropdownItems);
            }
        } else {
            setTestGroupFilter(typeof value === 'string' ? value.split(',') : value);
        }
    };
    useEffect(() => {
        if (!asset.deviceListLoading && asset.deviceListSuccess) {
            const deviceDetails = asset.deviceList.detail.filter((device) => device.systemId === props.deviceId)[0];
            setSystemDetail(deviceDetails);
            setSystemZones(deviceDetails.zoneDetails ?? []);
        } else if (!asset.deviceListLoading && asset.deviceListFail) {
            addToast(t('TOAST_MSG.DEVICE_LIST_API_FAILURE'), 'error');
        }
    }, [asset.deviceListLoading, asset.deviceListSuccess, asset.deviceListFail])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let numbers: Array<string> = [];

        let ATSDNo: Array<any> = Array.from(new Set(asset.luminaryList.detail.map(val => val.ATSD))).filter(val => val !== null && val !== undefined);
        setATSDFilterDropdownItems(ATSDNo);

        numbers = [];

        for (let index = 1; index <= 4; index++) {
            numbers.push(String(index));
        }

        setCircuitFilterDropdownItems(numbers);

        return () => {
            setATSDFilterDropdownItems([]);
            setCircuitFilterDropdownItems([]);
        }
    }, [props.deviceType, systemDetail, luminaryDataList]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.deviceId) {
            dispatch(Thunks.Asset.getLuminaryList(props.deviceId, '', rowsPerPage.toString(), DEFAULT_PAGE_ONE.toString(), [], [], statusFilterEnum, selectedSystemLines, selectedSystemLineZones, testGroupFilter.map(Number)));
        }
    }, [props.deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (asset.luminaryListSuccess && !asset.luminaryListLoading) {
            /* Debouncing to override user typing */
            const delayDebounceFn = setTimeout(() => {
                setCurrentPage(0);
                dispatch(Thunks.Asset.getLuminaryList(props.deviceId, '', rowsPerPage.toString(), DEFAULT_PAGE_ONE.toString(), circuitFilter, ATSDFilter, statusFilterEnum, selectedSystemLines, selectedSystemLineZones, testGroupFilter.map(Number)));
            }, 1000)
            return () => clearTimeout(delayDebounceFn)
        }
    }, [ATSDFilter, circuitFilter, statusFilter, statusFilterEnum, testGroupFilter]); // eslint-disable-line react-hooks/exhaustive-deps


    const handleATSDFilterChange = (event: SelectChangeEvent<typeof ATSDFilter>) => {
        const { target: { value }, } = event;
        if (value.length > 1) {
            setCircuitFilter([]);
        }

        if (value.includes(t('FILTER.ALL'))) {
            if (ATSDFilter.length === ATSDFilterDropdownItems.length) {
                // If all options are already selected, unselect all.
                setATSDFilter([]);
            } else {
                // If not all options are selected, select all.
                setATSDFilter(ATSDFilterDropdownItems);
            }
        }

        else {
            // On autofill we get a stringified value.
            setATSDFilter(typeof value === 'string' ? value.split(',') : value,);
        }
    };

    const handleCircuitFilterChange = (event: SelectChangeEvent<typeof circuitFilter>) => {
        const { target: { value }, } = event;
        if (value.includes(t('FILTER.ALL'))) {
            if (circuitFilter.length === CircuitFilterDropdownItems.length) {
                // If all options are already selected, unselect all.
                setCircuitFilter([]);
            } else {
                // If not all options are selected, select all.
                setCircuitFilter(CircuitFilterDropdownItems);
            }
        }

        else {
            // On autofill we get a stringified value.
            setCircuitFilter(typeof value === 'string' ? value.split(',') : value,);
        }
    };


    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setCurrentPage(newPage);
        dispatch(Thunks.Asset.getLuminaryList(props.deviceId, '', rowsPerPage.toString(), (newPage + 1).toString(), circuitFilter, ATSDFilter, statusFilterEnum, selectedSystemLines, selectedSystemLineZones, testGroupFilter.map(Number)));
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value));
        setCurrentPage(0);
        dispatch(Thunks.Asset.getLuminaryList(props.deviceId, '', parseInt(event.target.value, 10).toString(), DEFAULT_PAGE_ONE.toString(), circuitFilter, ATSDFilter, statusFilterEnum, selectedSystemLines, selectedSystemLineZones, testGroupFilter.map(Number)));
    };

    useEffect(() => {
        setSystemLines(
            systemDetail.lineDetails?.map((line) => line) || []
        )
    }, [systemDetail]);

    useEffect(() => {
    setTestGroupFilter([]); // Reset test group filter when zones change
}, [selectedSystemLineZones]);

    useEffect(() => {
        if (props.deviceType) {
            if (
                [
                    DEVICE_TYPE.DEVICE_TYPE_DGS,
                    DEVICE_TYPE.DEVICE_TYPE_ATSPLUS,
                    DEVICE_TYPE.DEVICE_TYPE_LPSTAR,
                    DEVICE_TYPE.DEVICE_TYPE_ZBS,
                ].includes(props.deviceType as DEVICE_TYPE)
            ) {
                setLuminaryStatusList(
                    luminaryStatusEnum.filter((status) => {
                        return status.id !== '' + DEVICE_STATUS.STATUS_OFFLINE
                    })
                )
            } else {
                setLuminaryStatusList(luminaryStatusEnum);
            }
        }
    }, [props.deviceType])

    const lineSelectHandler = (values: string[]) => {
        handleLineChange(values);
    }

    const handleLineChange = (values: string[]) => {
        if (values.length === 1) {
            const zones = systemDetail.zoneDetails?.filter((zone) => {
                return zone.lineId.toString() === values[0].toString()
            });
            setSystemLineZones(zones || []);
        } else {
            setSystemLineZones([]);
            setSelectedSystemLineZones([]);
        }

        setSelectedSystemLines(values);
    }

    const multiStatusChangeHandler = (values: string[]) => {
        setStatusFilterEnum(values);
    }

    useEffect(() => {
        handleLineChange(props.selectedLines?.map((line) => line.toString()) || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedLines])

    useEffect(() => {
        dispatch(Thunks.Asset.getLuminaryList(props.deviceId, '', rowsPerPage.toString(), '1', [], [], statusFilterEnum, selectedSystemLines, selectedSystemLineZones, testGroupFilter.map(Number)));
    }, [selectedSystemLines, selectedSystemLineZones]); // eslint-disable-line react-hooks/exhaustive-deps

    const headCells: HeadCell[] = [
        { id: 'luminaryName', label: t('DEVICE_DETAILS.LUMINARY_NAME'), hideSorting: false },
        ...(props.deviceType === DEVICE_TYPE.DEVICE_TYPE_CGLINE ?
            [
                {
                    id: 'line',
                    label: (
                        <>
                            {t('DEVICE_DETAILS.LUMINARY_LINE')}
                            <FilterDropdown records={systemLines} changeHandler={lineSelectHandler} selectedValues={selectedSystemLines} />
                        </>
                    ),
                    hideSorting: false
                },
                {
                    id: 'zone',
                    label: (
                        <>
                            {t('DEVICE_DETAILS.LUMINARY_ZONE')}
                            <FilterDropdown records={systemLineZones} disabled={systemLineZones && systemLineZones.length === 0} changeHandler={(values) => setSelectedSystemLineZones(values)} selectedValues={selectedSystemLineZones} />
                        </>
                    ),
                    hideSorting: false
                },
                { id: 'shortAddress', label: t('DEVICE_DETAILS.LUMINARY_SHORT_ADDRESS'), hideSorting: false },
                {
                    id: 'status',
                    label: (
                        <>
                            {
                                t('DEVICE_DETAILS.LUMINARY_STATUS')
                            }
                            <FilterDropdown records={systemDetail.systemSubType === getDeviceType(DEVICE_TYPE_WIRELESS) ? luminaryBitStatusWirelessEnum : luminaryBitStatusEnum} changeHandler={multiStatusChangeHandler} selectedValues={statusFilterEnum} showSelectedCount />
                        </>
                    ),
                    hideSorting: true
                },
            ] : props.deviceType === DEVICE_TYPE.DEVICE_TYPE_WIRELESS ?
                [
                    {
                        id: 'zone',
                        label: (
                            <>
                                {t('DEVICE_DETAILS.LUMINARY_ZONE')}
                                <FilterDropdown records={systemZones ?? []} changeHandler={(values) => setSelectedSystemLineZones(values)} selectedValues={selectedSystemLineZones} />
                            </>
                        ),
                        hideSorting: false
                    },
                    { id: 'shortAddress', label: t('DEVICE_DETAILS.LUMINARY_SHORT_ADDRESS'), hideSorting: false },
                    {
                        id: 'testGroup',
                        label: (
                            <>
                                {t('DEVICE_DETAILS.TEST_GROUP')}
                                <MuiCore.FormControl sx={{ m: 1, maxWidth: 100 }}>
                                    <MuiCore.Select
                                        multiple
                                        value={testGroupFilter}
                                        onChange={handleTestGroupFilterChange}
                                        renderValue={(selected) => `(${selected.length})`}

                                        variant='standard'
                                        disableUnderline
                                        sx={{
                                            display: 'inline-flex',
                                            minWidth: 60,
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 'calc(4 * 50px)',
                                                    overflow: 'auto',
                                                },
                                            },
                                        }}
                                    >
                                        <MuiCore.MenuItem key={t('FILTER.ALL')} value={t('FILTER.ALL')}>
                                            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                <Checkbox checked={testGroupFilter.length === testGroupDropdownItems.length} />
                                                <MuiCore.ListItemText primary={t('FILTER.ALL')} />
                                            </div>
                                        </MuiCore.MenuItem>
                                        <Divider />
                                        {testGroupDropdownItems.map((name) => (
                                            <MuiCore.MenuItem key={name} value={name}>
                                                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                    <Checkbox checked={testGroupFilter.indexOf(name) > -1} />
                                                    <MuiCore.ListItemText primary={name} />
                                                </div>
                                            </MuiCore.MenuItem>
                                        ))}
                                    </MuiCore.Select>
                                </MuiCore.FormControl>
                            </>
                        ),
                        hideSorting: false
                    },
                    {
                        id: 'status',
                        label: (
                            <>
                                {
                                    t('DEVICE_DETAILS.LUMINARY_STATUS')
                                }
                                <FilterDropdown records={systemDetail.systemSubType === getDeviceType(DEVICE_TYPE_WIRELESS) ? luminaryBitStatusWirelessEnum : luminaryBitStatusEnum} changeHandler={multiStatusChangeHandler} selectedValues={statusFilterEnum} showSelectedCount />
                            </>
                        ),
                        hideSorting: true
                    },
                ] : [
                    {
                        id: 'ATSD',
                        label: (
                            <>
                                {t('DEVICE_DETAILS.LUMINARY_ATSD')}
                                <MuiCore.FormControl sx={{ m: 1, maxWidth: 100 }}>
                                    <MuiCore.Select
                                        disabled={asset.luminaryListLoading}
                                        open={openATSDFilter}
                                        onOpen={() => setOpenATSDFilter(true)}
                                        onClose={() => setOpenATSDFilter(false)}
                                        disableUnderline

                                        variant='standard'
                                        labelId="atsd-multiple-checkbox-label"
                                        id="atsd-multiple-checkbox"
                                        multiple
                                        value={ATSDFilter}
                                        onChange={handleATSDFilterChange}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 'calc(4 * 50px)', // 4 items * item height
                                                    overflow: 'auto',
                                                },
                                            },
                                        }}
                                        onMouseDown={(event) => {
                                            if (asset.luminaryListLoading) {
                                                event.preventDefault();
                                            } else {
                                                setOpenATSDFilter(true);
                                            }
                                        }}
                                    >
                                        <MuiCore.MenuItem key={t('FILTER.ALL')} value={t('FILTER.ALL')} style={{
                                            maxWidth: 199,
                                            height: 46,
                                            paddingLeft: 16,
                                            paddingRight: 16,
                                            alignItems: 'center',
                                        }} >
                                            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                <Checkbox checked={ATSDFilter.length === ATSDFilterDropdownItems.length} />
                                                <MuiCore.ListItemText style={{ fontWeight: 400, fontSize: 16 }} primary={t('FILTER.ALL')} />
                                            </div>
                                        </MuiCore.MenuItem>
                                        <Divider />
                                        {ATSDFilterDropdownItems.map((name) => (
                                            <MuiCore.MenuItem key={name} value={name} style={{
                                                display: 'flex',
                                                maxWidth: 199,
                                                height: 46,
                                                paddingLeft: 16,
                                                paddingRight: 16,
                                                alignItems: 'center',
                                            }}>
                                                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                    <Checkbox checked={ATSDFilter.indexOf(name) > -1} />
                                                    <MuiCore.ListItemText primary={name} style={{ width: 100, fontWeight: 400, fontSize: 16 }} />
                                                </div>
                                            </MuiCore.MenuItem>
                                        ))}
                                    </MuiCore.Select>
                                </MuiCore.FormControl>
                            </>
                        ),
                        hideSorting: false
                    },
                    {
                        id: 'circuit',
                        label: (
                            <>
                                {t('DEVICE_DETAILS.LUMINARY_CIRCUIT')}
                                <MuiCore.FormControl sx={{ m: 1, maxWidth: 100 }}>
                                    <MuiCore.Select
                                        disabled={!ATSDFilter.length || ATSDFilter.length > 1 || asset.luminaryListLoading}
                                        open={openCircuitFilter}
                                        onOpen={() => setOpenCircuitFilter(true)}
                                        onClose={() => setOpenCircuitFilter(false)}
                                        disableUnderline
                                        variant='standard'
                                        labelId="circuit-multiple-checkbox-label"
                                        id="circuit-multiple-checkbox"
                                        multiple
                                        value={circuitFilter}
                                        onChange={(event) => {
                                            if (!ATSDFilter.length || ATSDFilter.length > 1 || asset.luminaryListLoading) {
                                                event.preventDefault();
                                                return;
                                            }
                                            handleCircuitFilterChange(event);
                                        }}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 'calc(4 * 50px)', // 4 items * item height
                                                    overflow: 'auto',
                                                },
                                            },
                                        }}
                                        onMouseDown={(event) => {
                                            if (!ATSDFilter.length || ATSDFilter.length > 1 || asset.luminaryListLoading) {
                                                event.preventDefault();
                                                return;
                                            }
                                            setOpenCircuitFilter(true);
                                            event.stopPropagation();
                                        }}

                                    >
                                        <MuiCore.MenuItem key={t('FILTER.ALL')} value={t('FILTER.ALL')}
                                            style={{
                                                maxWidth: 199,
                                                height: 46,
                                                paddingLeft: 16,
                                                paddingRight: 16,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                <Checkbox checked={circuitFilter.length === CircuitFilterDropdownItems.length} />
                                                <MuiCore.ListItemText primary={t('FILTER.ALL')} />
                                            </div>
                                        </MuiCore.MenuItem>
                                        <Divider />
                                        {CircuitFilterDropdownItems.map((name) => (
                                            <MuiCore.MenuItem key={name} value={name} style={{
                                                display: 'flex',
                                                maxWidth: 199,
                                                height: 46,
                                                paddingLeft: 16,
                                                paddingRight: 16,
                                                alignItems: 'center',
                                            }}>
                                                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                                                    <Checkbox checked={circuitFilter.indexOf(name) > -1} />
                                                    <MuiCore.ListItemText primary={name} style={{ width: 100, fontWeight: 400, fontSize: 16 }} />
                                                </div>
                                            </MuiCore.MenuItem>
                                        ))}
                                    </MuiCore.Select>
                                </MuiCore.FormControl>
                            </>
                        ),

                        hideSorting: false
                    },
                    { id: 'shortAddress', label: t('DEVICE_DETAILS.LUMINARY_SHORT_ADDRESS'), hideSorting: false },
                    {
                        id: 'lmStatus', label: (
                            <>
                                {t('DEVICE_DETAILS.LUMINARY_STATUS')}
                                <FilterDropdown records={luminaryStatusList} changeHandler={multiStatusChangeHandler} selectedValues={[]} />
                            </>
                        ),
                        hideSorting: true
                    },
                ]
        ),
    ];

    const getLuminaryDataList = (list: LuminaryList): LuminaryDataList => {
        return list.map((data, luminary) => {
            return (
                {
                    luminaryId: data.luminaryId,
                    luminaryName: data.luminaryName,
                    circuit: data.circuit ? data.circuit : '--',
                    ATSD: data.ATSD,
                    testGroup: data.testGroupId,//for sorting
                    testGroupId: data.testGroupId,//for showing the test group
                    lmStatus: data.lmStatus,
                    shortAddress: data.shortAddress ? data.shortAddress : '--',
                    BitStatus: data.BitStatus ?? [],
                    line: data.line ?? '--',
                    zone: data.zone ?? '--',
                }
            );
        })
    };

    useEffect(() => {
        setSelectedLuminary(props.luminaryId)
    }, [props.luminaryId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!asset.luminaryListLoading && asset.luminaryListSuccess) {
            if (asset.luminaryList.detail) {
                setEmptyLuminaryList(asset.luminaryList.detail.length <= 0)
                setLuminaryList(asset.luminaryList.detail.slice(0, rowsPerPage));
                setLuminaryDataList(getLuminaryDataList(asset.luminaryList.detail.slice(0, rowsPerPage)));
                if (asset.luminaryList.totalRecordCount) {
                    setTotalRecords(asset.luminaryList.totalRecordCount);
                }
            }
        } else if (!asset.luminaryListLoading && asset.luminaryListFail) {
            setEmptyLuminaryList(true);
        }
    }, [asset.luminaryListLoading, asset.luminaryListSuccess, asset.luminaryListFail]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: any): void => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    const sortedValue = stableSort(luminaryDataList, getSortComparator(newOrder, property));
    setLuminaryDataList(sortedValue);
};

    const handleDisplay = (value: {
        luminaryId: string;
    }): void => {
        setSelectedLuminary(value.luminaryId)
        //*** Might need in furure ***//
        props.luminaryClick({ luminaryId: value.luminaryId });
    };

    const setSearchText = (searchText: string): void => {
    };

    return (
        <>
            <Card style={{ flex: '1 1 0px', overflow: 'auto' }} id="device-luminaries-list-container" data-testid="device-luminaries-list-container">

                <ListSubheader
                    color="primary"
                    component="div"
                    data-testid="luminaries-list-header"
                    id="luminaries-list-header"
                    style={{
                        display: 'flex',
                        fontWeight: 600,
                        fontSize: 14,
                        alignItems: 'center',
                        overflow: 'auto',
                        whiteSpace: 'nowrap',
                        marginLeft: 10,
                        height: 50,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        style={{
                            alignItems: 'left',
                            color: PXBColors.blue[400],
                            width: '92.3%',
                            paddingLeft: '10',
                        }}
                        id="luminaries-title"
                        data-testid="luminaries-title"
                    >
                        {t('DEVICE_DETAILS.LUMINARIES_COUNTS') + ' (' + asset.luminaryList.totalRecordCount + ')'}
                    </Typography>

                    <ListItemButton
                        id="luminaries-list-header-search"
                        data-testid="luminaries-list-header-search"
                        style={{
                            width: '7.41%',
                            height: 50,
                            display: 'none'  //TODO- hide serach till API ready
                        }}
                    >
                        <MuiCore.IconButton style={{ alignItems: 'right', width: '10%' }} id="Luminary-search" onClick={(): void => setSearchText('Test')}> <MuiIcons.Search style={{ color: PXBColors.blue[400] }} /> </MuiCore.IconButton>
                    </ListItemButton>

                    <ListItemButton
                        onClick={() => { setOpenList(!openList) }}
                        id="list-header-item-button"
                        style={{
                            width: '7.41%',
                        }}
                    >
                        {openList ? (
                            <KeyboardArrowUp data-testid="arrow-up" id="list-header-up-icon" />
                        ) : (
                            <KeyboardArrowDown data-testid="arrow-down" id="list-header-down-icon" />
                        )}
                    </ListItemButton>
                </ListSubheader>

                <Divider />

                <Collapse in={openList} timeout="auto" unmountOnExit>

                    <Table stickyHeader size="small"
                        id="luminaries-list-table"
                        data-testid="luminaries-list-table">
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                        />

                        {!asset.luminaryListLoading && !emptyLuminaryList && <TableBody className="tableHover" style={{ minHeight: 400 }} key={'mainTable'}
                            id="luminaries-list-table-body"
                            data-testid="luminaries-list-table-body">
                            {luminaryDataList.map((row: LuminaryData, index: number) => (
                                <LuminaryListItem
                                    key={row.luminaryId}
                                    row={row}
                                    selectedLuminary={selectedLuminary}
                                    displayClick={handleDisplay}
                                    deviceType={props.deviceType}
                                ></LuminaryListItem>
                            ))}
                        </TableBody>}
                    </Table>
                    {!emptyLuminaryList && !asset.luminaryListLoading && <TablePagination
                        component="div"
                        data-testid="pagination-lum"
                        SelectProps={{
                            MenuProps: {
                                sx: {
                                    '.MuiTablePagination-menuItem': {
                                        display: 'flex',
                                        flexDirection: 'column'
                                    },
                                },
                            },
                        }}
                        count={totalRecords}
                        page={currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t("LABELS.ITEMS_PER_PAGE")}
                        showFirstButton showLastButton
                    />}

                    {asset.luminaryListLoading && <EmptyState style={{ flex: 1, minHeight: 150, backgroundColor: PXBColors.white[50] }}
                        icon={<MuiCore.CircularProgress id="progress-spinner" />} title="" placeholder={undefined} />}
                    {!asset.luminaryListLoading && emptyLuminaryList && <EmptyState style={{ flex: 1, height: 200 }}
                        title={t('ORGANIZATION_MANAGE.ERROR_NO_LUMINARY_FOUND')} icon={undefined} placeholder={undefined} />}

                </Collapse>
            </Card >
        </>
    );
};
